import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import api from "../../api";

const stamp = (value) => value ? new Date(/[Zz]$|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`).toLocaleString() : "—";
export default function ActivityHistory({ projectId }) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const through = useRef(null);
  useEffect(() => {
    let active = true;
    setResult(null); setError(false);
    const params = new URLSearchParams({ q: query, offset: page * 25, limit: 25 });
    if (through.current !== null) params.set("through_id", through.current);
    api({ method: "get", url: `/surveys/projects/${projectId}/activity?${params}` })
      .then(({ data }) => {
        if (!active) return;
        through.current = data.through_id;
        setResult(data);
      })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [projectId, query, page, retry]);
  const refresh = () => { through.current = null; setPage(0); setRetry((n) => n + 1); };
  return <Card><CardContent>
    <Typography variant="h6">Project audit trail</Typography>
    <Typography variant="body2">Newest first. Paging keeps the initial event cutoff; refresh to include newer activity. This is a review view, not a frozen audit export.</Typography>
    <Box component="form" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); refresh(); }} sx={{ my: 2 }}>
      <TextField label="Search action, note or SM-number" size="small" fullWidth value={search}
        onChange={(e) => setSearch(e.target.value)} inputProps={{ maxLength: 200 }} />
      <Button type="submit">Search activity</Button>
      <Button onClick={() => { setSearch(""); setQuery(""); refresh(); }}>Clear</Button>
      <Button onClick={refresh}>Refresh activity</Button>
    </Box>
    {error ? <Alert severity="error">Unable to load activity.
      <Button onClick={() => setRetry((n) => n + 1)}>Retry</Button>
    </Alert> : !result ? <CircularProgress size={22} aria-label="Loading activity" /> : <>
      <Typography aria-live="polite">{result.total ? `${page * 25 + 1}–${Math.min((page + 1) * 25, result.total)}` : "0"} of {result.total} events</Typography>
      {!result.items.length && <Typography>No matching activity.</Typography>}
      {result.items.map((event) => <Box key={event.id} sx={{ py: 2, borderBottom: "1px solid #e5ebf1", overflowWrap: "anywhere" }}>
        <Typography>{event.action.replaceAll(".", " · ")}{event.assignment_id ? ` — SM-${event.assignment_id}` : ""}</Typography>
        <Typography variant="caption">{stamp(event.created_at)} · actor #{event.actor_id} · event #{event.id}</Typography>
        {event.detail && <Typography sx={{ whiteSpace: "pre-wrap" }}>{event.detail}</Typography>}
        {event.payload?.answers && <Box component="details"><summary>Answer revision {event.payload.revision}</summary>
          <Box component="pre" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontSize: 13 }}>{JSON.stringify(event.payload.answers, null, 2)}</Box>
        </Box>}
      </Box>)}
      <Stack direction="row"><Button disabled={!page} onClick={() => setPage(page - 1)}>Previous events</Button>
        <Button disabled={(page + 1) * 25 >= result.total} onClick={() => setPage(page + 1)}>Next events</Button></Stack>
    </>}
  </CardContent></Card>;
}
